/* eslint-disable no-underscore-dangle */
import { useMemo, useState, useCallback, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useParams, useLocation, useHistory } from "react-router";
import { gql, useQuery, useLazyQuery } from "@apollo/client";

import Layout from "../../../components/Layout";
import Loader from "../../../components/Loader";
import TabNavigation from "../../../components/TabNavigation";
import ProfilePreview from "../../../components/UserInterface/ProfilePreview";
import styles from "./index.module.scss";
import Table from "../../../components/Table";
import StatusFilterDropdown  from "../../../components/FilterDropdowns/StatusFilterDropdown";
import CepFilterDropdown, { cepList } from "../../../components/FilterDropdowns/CepFilterDropdown";
import SearchInput from "../../../components/SearchInput";
import { ClientCell, DateCell, BooleanCell, ProfileListCell, StatusCell } from "../../../components/Table/TableCells";
import EmptyView from "../../../components/Table/EmptyView";
import { useStoreContext } from "../../../contexts/StoreProvider";
import UserProfile from "../../../components/User/UserProfile";
import Button from "../../../components/Button";
import { statusList } from "../../../utils/status";
import { FilterType } from "../../../types/contextType";

const GET_USER = gql`
  query User($id: ID!) {
    user(id: $id) {
      first_name
      last_name
      id
      phone
      phone_ext
      email
      adp_auth_num
      role {
        type
        name
      }
      organization {
        name
        address {
          address_1
          address_2
          city
          postal_code
          province
        }
      }
      locations {
        id
        name
        adp_auth_num
        address {
          address_1
          address_2
          city
          postal_code
          province
        }
      }
    }
  }
`;

const GET_CLIENTS = gql`
  query Clients(
    $limit: Int
    $sort: String
    $start: Int
    $where: JSON
    $connectionWhere: JSON
  ) {
    clients(limit: $limit, sort: $sort, start: $start, where: $where) {
      updated_at
      id
      first_name
      last_name
      health_card
      health_card_version
      cep
      client_files {
        id
        status
        updated_at
      }
      sales_reps {
        id
        first_name
        last_name
        locations {
          id
          name
          adp_auth_num
          address {
            name
            address_1
            address_2
            city
            postal_code
            province
          }
        }
        role {
          name
        }
      }
      authorizers {
        id
        first_name
        last_name
        role {
          name
        }
      }
    }
    clientsConnection(where: $connectionWhere) {
      aggregate {
        count
      }
    }
  }
`;

type WhereType = {
  [key: string]:
    | string
    | { [key: string]: boolean | string | string[] }[]
    | string[];
};

const UserPage = () => {
  const { state: { user, filter }, dispatch } = useStoreContext();
  const params = useParams<{ user_id: string }>();
  const [filters, setFilters] = useState<FilterType>({status: null, cep: null});
  const location = useLocation();
  const history = useHistory();
  const [pageSize, setPageSize] = useState(10);
  const [, setBoundaryError] = useState<Error | null>(null);
  const [search, setSearch] = useState<string | undefined>();
  const { data, loading, error } = useQuery(GET_USER, {
    fetchPolicy: "no-cache",
    variables: {
      id: params.user_id,
    },
    skip: !params || !params.user_id,
  });
  const [getClients, { data: clientData, loading: clientLoading }] =
    useLazyQuery(GET_CLIENTS, {
      onError: (error) => {
        setBoundaryError(() => {
          throw error;
        });
      },
    });

    useEffect(() => {
      setFilters(filter);
    }, [filter]);

  const role = data?.user.role.type;
  const isAdmin = user?.role.type === "administrator";

  const handleFilterChange = (
    id: string,
    filter?:
      | { value: string; label: string }
      | { value: string; label: string }[]
  ) => {
    const newFilter = {
      [id]: Array.isArray(filter) ? filter.map((f) => f.value) : filter?.value,
    };

    setFilters({ ...filters, ...newFilter });
  };

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sortBy }) => {
      const variables: {
        limit: number;
        start: number;
        sort?: unknown;
        where?: WhereType;
        connectionWhere?: WhereType;
      } = {
        limit: pageSize,
        start: pageIndex * pageSize,
      };
      if (sortBy.length) {
        if (sortBy[0].id === "status") {
          variables.sort = `client_files.status:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "sales_reps") {
          variables.sort = `sales_reps.first_name:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "authorizers") {
          variables.sort = `authorizers.first_name:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "updated") {
          variables.sort = `client_files.updated_at:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else {
          variables.sort = `${sortBy[0].id}:${sortBy[0].desc ? "DESC" : "ASC"}`;
        }
      }

      let where: WhereType = {};
      const and: { [key: string]: boolean | string | string[] }[] = [];
      const or: { [key: string]: boolean | string }[] = [];

      if (role === "authorizer" || role === "motion_support") {
        where = { authorizers_in: [params.user_id] };
      }
      if (role === "sales_rep") {
        where = { sales_reps_in: [params.user_id] };
      }
      variables.connectionWhere = where;
      if (filter.cep || filter.status || search) {
        if (filter.cep) {
          and.push({ cep: filter.cep === "yes" });
        }
        if (filter.status) {
          and.push({ "client_files.status": filter.status });
        }
        if (search) {
          or.push({ first_name_contains: search });
          or.push({ last_name_contains: search });
        }
        variables.start = 0;
      }
      if (and.length) where._and = and;
      if (or.length) where._or = or;
      variables.where = where;

      setPageSize(pageSize);
      getClients({
        variables,
      });
    },
    [getClients, filter, search, params.user_id, role]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => <ClientCell client={row.original} />,
      },
      {
        Header: "Updated",
        accessor: "updated",
        Cell: ({ row }) => (
          <DateCell
            value={
              row.original?.client_files?.sort(
                (a, b) =>
                  new Date(b.updated_at).getTime() -
                  new Date(a.updated_at).getTime()
              )?.[0]?.updated_at
            }
          />
        ),
      },
      {
        Header: "CEP Client",
        accessor: "cep",
        Cell: ({ value }) => <BooleanCell value={value} />,
      },
      {
        Header: `${(role === "authorizer" || role === "motion_support") ? "Motion Team" : "Authorizer(s)"}`,
        accessor: (role === "authorizer" || role === "motion_support") ? "sales_reps" : "authorizers",
        Cell: ({ row }) => {
          const cellData = (role === "authorizer" || role === "motion_support") ? row.original.sales_reps : row.original.authorizers;

          return <ProfileListCell data={cellData} admin link />;
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => <StatusCell files={row.original.client_files} />,
      },
    ],
    [role]
  );

  const getCurrentFilters = useMemo(() => {
    const currentFilters = Object.keys(filters)
      .filter((f) => !!filters[f])
      .map((filter) => {
        let id;
        if (filter === "cep") {
          id = "cep";
        }
        if (filter === "status") {
          id = "status";
        }
        return { id, value: filters[filter] };
      });

    if (search) {
      currentFilters.push({ id: "first_name", value: search });
    } else {
      currentFilters.filter((f) => f.id !== "first_name");
    }
    return currentFilters;
  }, [filters, search]);

  const pageCount = clientData ? Math.ceil(clientData.clientsConnection.aggregate.count / pageSize) : 0;

  const handleResetFilters = () => {
    setFilters({cep: null, status: null});
    dispatch({
      type: "FILTER",
      payload: {status: null, cep: null},
    });

    setSearch("");
    const input = (document.getElementById("search-filter") as HTMLInputElement);
    if (input) input.value = "";
  };

  return (
    <Layout>
      <Helmet>
        <title>User</title>
      </Helmet>
      <div className={styles.container}>
        {error && error.message}
        {loading ? (
          <div className={styles.loader}>
            <Loader heading="Fetching user..." />
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <ProfilePreview
                size="large"
                user={data?.user}
                userRole
                edit="Edit profile"
                editTo={`${data?.user.id}/edit`}
              />
            </div>
            <div className={styles.nav}>
              {isAdmin ? (
                <TabNavigation
                  links={[
                    {
                      url: `/admin/user/${params.user_id}`,
                      text: "User Profile",
                    },
                    {
                      url: `/admin/user/${params.user_id}#clients`,
                      text: "Clients",
                    },
                  ]}
                />
              ) : (
                <TabNavigation
                  links={[
                    {
                      url: `/user/${params.user_id}`,
                      text: "User Profile",
                    },
                  ]}
                />
              )}
            </div>
          </>
        )}
        {location.hash === "#clients" ? (
          <div>
            <div className={styles.filterRow}>
              <div className={styles.filterItem}>
                <SearchInput
                  onChange={(value) => setSearch(value)}
                  placeholder="Search by name"
                  loading={loading}
                />
              </div>
              <div className={styles.filterItem}>
                <StatusFilterDropdown
                  onChange={(status: { value: string; label: string }) => handleFilterChange("status", status) }
                  value={statusList.find((status) => status.value === filters.status)}
                />
              </div>
              <div className={styles.filterItem}>
                <CepFilterDropdown
                  onChange={(enabled: { value: string; label: string }) => handleFilterChange("cep", enabled) }
                  value={cepList.find((cep) => cep.value === filters.cep)}
                />
              </div>
              <div>
                <Button
                  secondary
                  onClick={handleResetFilters}
                >
                  Reset Filters
                </Button>
              </div>
            </div>
            <Table
              data={clientData?.clients || []}
              columns={columns}
              pageCount={pageCount}
              fetchData={fetchData}
              handleRowClick={(row) => {
                history.push(`/admin/client/${row.original.id}`);
              }}
              total={clientData?.clientsConnection.aggregate.count || 0}
              loading={clientLoading}
              emptyView={
                <EmptyView message="No results found matching your search criteria." />
              }
              initialState={{ sortBy: [{ id: "first_name", desc: false }] }}
              filters={getCurrentFilters}
            />
          </div>
        ) : data?.user ? (
          <UserProfile user={data?.user} />
        ) : null}
      </div>
    </Layout>
  );
};

export default UserPage;
